import { useEffect, useRef, useState } from "react";
import { isUnavailableStoredUrl, toFileRoutePath } from "./fileRoute";
import { fetchStoredFile } from "./storedFileFetcher";

export type StoredFileStatus = "idle" | "loading" | "ready" | "error";

/**
 * A loadable `src` for `url` (story 25240): a stored file → object URL of bytes
 * fetched through /api/files; a storage URL with no source → error (never loaded
 * directly); anything else (data:, blob:, other hosts) → `url` itself. Keyed on
 * the file path, so a re-signed URL or the new reference for the same blob
 * reuses the fetch — this replaces useStableBlobUrl.
 */
export function useStoredFileObjectUrl(url: string | null | undefined): { src: string | null; status: StoredFileStatus } {
  const path = toFileRoutePath(url);
  const [state, setState] = useState<{ path: string | null; src: string | null; status: StoredFileStatus }>({
    path: null,
    src: null,
    status: "idle",
  });

  // Object URLs we have revoked; a stale state may still hold one for a render.
  const revoked = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!path) return;
    const revokedUrls = revoked.current;
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ path, src: null, status: "loading" });
    fetchStoredFile(path).then(
      (blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ path, src: objectUrl, status: "ready" });
      },
      () => {
        if (!cancelled) setState({ path, src: null, status: "error" });
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) {
        revokedUrls.add(objectUrl);
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [path]);

  if (!url) return { src: null, status: "idle" };
  if (!path) return isUnavailableStoredUrl(url) ? { src: null, status: "error" } : { src: url, status: "ready" };
  if (state.path !== path || (state.src && revoked.current.has(state.src))) return { src: null, status: "loading" };
  return { src: state.src, status: state.status };
}
